<template>
  <div class="tag-view-container flex items-center h-full gap-2 px-2">
    <!-- 左滚动按钮 - 只在需要滚动时显示 -->
    <button
      v-if="needScroll"
      class="tag-nav-btn tag-nav-prev"
      :class="{ disabled: !canScrollLeft }"
      @click="scrollLeft"
      :disabled="!canScrollLeft"
      title="向左滚动"
    >
      <i class="iconfont icon-left"></i>
    </button>

    <!-- 滚动容器 -->
    <div class="tag-scroll-wrapper flex-1 overflow-hidden">
      <div
        ref="tagScrollRef"
        class="tag-scroll-content flex gap-2"
        :style="{ transform: `translateX(${scrollOffset}px)` }"
      >
        <RightMenu :MenuNode="TagMenu" :menuInfo="item" :zIndex="9999" v-for="item in tagList" :key="item.name">
          <el-button
            class="px-5 tag-item flex-shrink-0"
            @click="toView(item)"
            @contextmenu.prevent
            :type="selectdTag?.name === item.name ? 'primary' : 'default'"
          >
            <i class="pr-1 iconfont" :class="`${item.icon}`" v-if="item.icon"></i>
            {{ item.title }}
            <i
              class="pl-1 iconfont icon-close close"
              title="关闭页面"
              v-if="!item.allowShow"
              @click.stop="onClose(item)"
            ></i>
          </el-button>
        </RightMenu>
      </div>
    </div>

    <!-- 右滚动按钮 - 只在需要滚动时显示 -->
    <button
      v-if="needScroll"
      class="tag-nav-btn tag-nav-next"
      :class="{ disabled: !canScrollRight }"
      @click="scrollRight"
      :disabled="!canScrollRight"
      title="向右滚动"
    >
      <i class="iconfont icon-right"></i>
    </button>
  </div>
</template>

<script lang="ts" setup>
import { useTagsViewStore } from '@/store/modules/tagsView';
import RightMenu from '@/components/right-menu/index.vue';
import { computed, watch, ref, nextTick, onMounted, onUnmounted } from 'vue';
import { RouteLocationNormalizedLoaded, useRouter } from 'vue-router';
import TagMenu from './TagMenu.vue';
import { MenuItemDto, MenuMaps } from '@/router/modules';

const router = useRouter();
const tagStore = useTagsViewStore();

const tagList = computed(() => tagStore.getVisitedViews);
const selectdTag = computed(() => tagStore.getSelectedTag);

const tagScrollRef = ref<HTMLElement>();
const scrollOffset = ref(0);
const canScrollLeft = ref(false);
const canScrollRight = ref(false);
const needScroll = ref(false);

const SCROLL_STEP = 200; // 每次滚动的距离

// 更新滚动按钮状态
const updateScrollState = () => {
  if (!tagScrollRef.value) return;

  const scrollContent = tagScrollRef.value;
  const wrapper = scrollContent.parentElement;
  if (!wrapper) return;

  const contentWidth = scrollContent.scrollWidth;
  const wrapperWidth = wrapper.clientWidth;

  // 判断是否需要显示滚动按钮：内容宽度 > 容器宽度
  needScroll.value = contentWidth > wrapperWidth;

  // 可以向左滚动：当前偏移量 < 0
  canScrollLeft.value = scrollOffset.value < 0;
  // 可以向右滚动：内容宽度 > 包装器宽度 + 当前偏移量
  canScrollRight.value = contentWidth + scrollOffset.value > wrapperWidth;
};

// 向左滚动
const scrollLeft = () => {
  scrollOffset.value = Math.min(scrollOffset.value + SCROLL_STEP, 0);
  nextTick(() => updateScrollState());
};

// 向右滚动
const scrollRight = () => {
  if (!tagScrollRef.value) return;
  const scrollContent = tagScrollRef.value;
  const wrapper = scrollContent.parentElement;
  if (!wrapper) return;

  const contentWidth = scrollContent.scrollWidth;
  const wrapperWidth = wrapper.clientWidth;
  const maxScroll = -(contentWidth - wrapperWidth);

  scrollOffset.value = Math.max(scrollOffset.value - SCROLL_STEP, maxScroll);
  nextTick(() => updateScrollState());
};

// 监听标签列表变化，更新滚动状态
watch(
  () => tagList.value,
  () => {
    nextTick(() => updateScrollState());
  },
  { deep: true }
);

// 组件挂载时初始化滚动状态
onMounted(() => {
  nextTick(() => updateScrollState());
  window.addEventListener('resize', updateScrollState);
});

// 组件卸载时移除监听
onUnmounted(() => {
  window.removeEventListener('resize', updateScrollState);
});

// 监听路由变化
watch(
  () => router.currentRoute.value,
  (route: RouteLocationNormalizedLoaded) => {
    if (route.path.startsWith('/redirect/')) {
      return;
    }
    const menu = MenuMaps.get(route.name);
    let path = '';
    if (menu.parentName) {
      const parent = MenuMaps.get(menu.parentName);
      path = parent.path;
    } else {
      path = menu.path;
    }
    tagStore.setSelectedByPath(path);
  },
  {
    immediate: true,
  }
);

const toView = (item: MenuItemDto) => {
  tagStore.setSelectedTag(item);
  router.push(item.path);
};

const onClose = (item: MenuItemDto) => {
  if (item.allowShow) {
    return; // 不允许关闭
  }
  tagStore.closeView(item);
};
</script>

<style lang="less">
.tag-scroll-wrapper {
  position: relative;
}

.tag-scroll-content {
  transition: transform 0.3s ease;
  will-change: transform;
}

.tag-nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid var(--el-border-color);
  background-color: var(--el-fill-color-light);
  color: var(--el-text-color-primary);
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover:not(.disabled) {
    background-color: var(--el-fill-color);
    border-color: var(--el-border-color-hover);
    color: var(--el-color-primary);
  }

  &:not(.disabled) {
    pointer-events: auto;
  }

  &:active:not(.disabled) {
    background-color: var(--el-color-primary-light-7);
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }

  i {
    font-size: 14px;
  }
}

.tag-item {
  position: relative;
  white-space: nowrap;

  .close {
    font-size: 13px;
    position: absolute;
    right: 4px;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.2s;
  }

  &:hover {
    .close {
      opacity: 1;
    }
  }
}
</style>
